import React, { useContext } from 'react';
import { Col, Collapse, Row } from 'reactstrap';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Dinero from 'dinero.js';
import { useTranslation } from 'react-i18next';
import ImageContainer from './ImageContainer';
import { OrderContext } from '../context/OrderContext';
import ArticleQuantity from './ArticleQuantity';
import { CampaignContext } from '../context/CampaignWrapper';

import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import ImagePlaceholder from '../assets/images/image-placeholder.svg';
import { isRegularArticle } from '../utils/helpers';

const ARTICLES_PREVIEW_IMAGE_SIZE = { width: 95, height: 95 };

const ArticlesPreview = () => {
  const { campaign: { articles } } = useContext(CampaignContext);
  const {
    order: { quantities }, setArticleQuantity, removeArticleFromCart,
  } = useContext(OrderContext);
  const [translate] = useTranslation();

  const totalPrice = articles?.reduce((sum, { _id, price }) => sum + ((quantities[_id] || 0) * price), 0);
  const isOpen = !!totalPrice;

  const cloudinaryParams = ['c_pad'];

  const iconStyle = { verticalAlign: 'top', cursor: 'pointer' };

  const articlesPreview = articles?.filter(isRegularArticle).map((article) => {
    const {
      _id: articleId, name, imageUrls, price: p,
    } = article;

    const quantity = quantities[articleId] || 0;
    const isArticleVisible = !!quantity;

    const articlePrice = Dinero({ amount: p || 0 }).toFormat();
    const price = Dinero({ amount: p || 0 }).multiply(quantity).toFormat();
    const image = imageUrls?.[0] || undefined;

    const changeArticleQuantity = (count: number) => {
      setArticleQuantity(articleId, count);
    };

    const removeArticle = () => {
      removeArticleFromCart(articleId);
    };

    return (
      <Collapse isOpen={isArticleVisible} key={articleId}>
        <div className="articles-preview-article border-bottom">
          <div className="p-0">
            <ImageContainer
              src={image}
              className="rounded"
              size={ARTICLES_PREVIEW_IMAGE_SIZE}
              params={cloudinaryParams}
              alt={name}
              placeholder={ImagePlaceholder}
            />
          </div>
          <div className="flex-grow-1">
            <Row className="h-100 justify-content-between gap-3 m-0">
              <Col xs={12} className="ps-3">
                <Row className="justify-content-between flex-wrap">
                  <Col xs={10}>
                    <h6 className="fw-bold m-0">{name}</h6>
                  </Col>
                  <Col xs={2} className="trash-icon-container text-end px-0">
                    <TrashIcon style={iconStyle} onClick={removeArticle} />
                  </Col>
                  <Col xs={12}>
                    <p className="m-0">{articlePrice}</p>
                  </Col>
                </Row>
              </Col>
              <Col xs={12} className="ps-3 mt-auto">
                <Row className="align-items-end justify-content-between">
                  <Col>
                    <ArticleQuantity
                      quantity={quantity}
                      setQuantity={changeArticleQuantity}
                    />
                  </Col>
                  <Col className="text-end px-0">
                    <p className="articles-preview-price">{price}</p>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </div>
      </Collapse>
    );
  });

  const shippingCostsArticle = articles?.find((a) => a.__typename === 'ShippingCostsArticle');
  const donationArticle = articles?.find((a) => a.__typename === 'DonationArticle');
  const totalLines = [
    ...(quantities[shippingCostsArticle?._id || ''] ? [{
      label: `${translate('COMMON.SHIPPING_COST')}:`,
      value: Dinero({ amount: shippingCostsArticle?.price }).toFormat(),
    }] : []),
    ...(quantities[donationArticle?._id || ''] && donationArticle?.price ? [{
      label: `${translate('COMMON.DONATION')}:`,
      value: Dinero({ amount: quantities[donationArticle._id] }).multiply(donationArticle.price).toFormat(),
    }] : []),
    {
      label: translate('COMPONENTS.ARTICLES_PREVIEW.TOTAL'),
      value: Dinero({ amount: totalPrice }).toFormat(),
    }];

  return (
    <Collapse isOpen={isOpen}>
      <div className="articles-preview-list">
        {articlesPreview}
        {totalLines.map(({ label, value }) => (
          <div className="d-flex justify-content-between pt-4 px-sm-0 align-items-baseline" key={label}>
            <h5 className="articles-preview-total" style={{ color: '#525F7F', fontSize: '1rem' }}>
              {label}
            </h5>
            <h5 className="articles-preview-total-price">{value}</h5>
          </div>
        ))}
      </div>
    </Collapse>
  );
};

export default ArticlesPreview;
